import React, { ChangeEvent, useState, useCallback, useRef } from 'react';
import './style.scss';
import RectangleIcon from '../icons/RectangleIcon';
import useOutsideClick from '../../hooks/useOutsideClick';

export type SourceOption = {
  value: string;
  body: string;
  disabled?: boolean;
};

export type PickerProps = {
  options: SourceOption[];
  value: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
};

const Picker: React.FC<PickerProps> = ({
  options,
  value,
  onChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOutsideClick(dropdownRef, handleClose);

  const handleOptionClick = useCallback(
    (newValue: string) => {
      if (!options.find((option) => option.value === newValue)?.disabled) {
        onChange({
          target: { value: newValue },
        } as ChangeEvent<HTMLSelectElement>);
        setIsOpen(false);
      }
    },
    [onChange, options]
  );

  return (
    <div
      className={`dropdown ${className} `}
      onClick={() => setIsOpen(!isOpen)}
      ref={dropdownRef}
    >
      <div className={`selected ${isOpen && 'selected_active'}`}>
        {options.find((option) => option.value === value)?.body ||
          'Select an option'}
      </div>
      <RectangleIcon
        className={`dropdown__rectangle ${isOpen && 'dropdown__rectangle_active'}`}
      />
      {isOpen && (
        <ul className="options">
          {options.map(({ value, body, disabled = false }) => (
            <li
              key={value}
              className={`option ${disabled ? 'disabled' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick(value);
              }}
            >
              {body}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default React.memo(Picker);
